/**
 * The campaign's identity line and its two dangerous buttons.
 *
 * It is pinned to the top of the scroll area on purpose. The tabs below it are
 * long forms, and the previous version put Save at the top of a page you then
 * scrolled away from - so the state of your unsaved work left the screen at
 * exactly the moment you were making more of it. Pinned, the campaign's name,
 * its status and whether it is saved are answerable without scrolling back.
 *
 * It pins to the top of the content column and publishes its own height so the
 * audience rail can pin under it in turn - see crm/stickyChrome.ts. The height
 * is not a constant: the header wraps on a narrow window and grows by a line
 * when there is unsaved work.
 *
 * The three counters are the campaign's own record and are rendered as figures
 * rather than as the grey sentence they used to be: how many letters this thing
 * has actually put in the world is not a caption.
 */
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Loader2, Pause, Play, RotateCcw, Save } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { contactedTotal } from "../lib";
import type { Campaign } from "../../../../lib/marketing";
import { PINNED_HEADER_TOP, usePinnedHeaderHeight } from "../../../stickyChrome";
import { Btn } from "../../../ui";
import { StatusPill, num } from "../shared";

function Counter({ label, value, tone }: { label: string; value: number; tone?: "good" | "muted" }) {
  return (
    <div className="flex flex-col">
      <span
        className="crm-tnum text-[15px] font-bold leading-none"
        style={{
          color:
            tone === "good" && value > 0
              ? "var(--color-success)"
              : tone === "muted"
                ? "var(--color-muted-foreground)"
                : undefined,
        }}
      >
        {num(value)}
      </span>
      <span className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
    </div>
  );
}

export function CampaignHeader({
  campaign,
  canManage,
  dirty,
  saving,
  blocked,
  onSave,
  onDiscard,
  onToggleStatus,
}: {
  campaign: Campaign;
  canManage: boolean;
  dirty: boolean;
  saving: boolean;
  /** True when the readiness checklist has an open blocking item. Activate
   *  would 422, so the button says why instead of finding out. */
  blocked: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onToggleStatus: () => void;
}) {
  const t = useT();
  const ref = useRef<HTMLElement>(null);
  usePinnedHeaderHeight(ref);
  const running = campaign.status === "active";
  // Pausing is always allowed; only starting has preconditions.
  const toggleDisabled = !running && blocked;

  return (
    // The bleed is wider than the page gutter so the strip covers the cards'
    // shadows as they pass under it; without it a row's shadow shows through
    // the blur at the header's edge.
    <header
      ref={ref}
      className={cn(
        PINNED_HEADER_TOP,
        "-mx-2 mb-3.5 border-b border-[var(--crm-hair)] bg-background/85 px-2 pb-3.5 pt-1 backdrop-blur",
      )}
    >
      <Link to="/marketing/campaigns" className="crm-sub -ml-1 inline-flex items-center gap-1 hover:text-foreground">
        <ChevronLeft className="size-4" />
        {t("marketing.back")}
      </Link>

      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="crm-h1 crm-trunc">{campaign.name}</h1>
          <StatusPill status={campaign.status} label={t(`marketing.status.${campaign.status}`)} />
          <span className="crm-sub crm-tnum">/{campaign.slug}</span>
        </div>

        <div className="flex flex-wrap items-center gap-5">
          {/* Hidden on a phone. A pinned header that eats half the viewport
              stops being a header, and these three numbers are also on the
              campaign list one tap away. */}
          <div className="hidden items-center gap-6 border-r border-[var(--crm-hair)] pr-6 sm:flex">
            {/* The sum, for the same reason as the list: `sent` on its own
                falls as answers arrive. */}
            <Counter label={t("marketing.col.sent")} value={contactedTotal(campaign)} />
            <Counter label={t("marketing.col.skipped")} value={campaign.skipped} tone="muted" />
            <Counter label={t("marketing.col.replied")} value={campaign.replied} tone="good" />
          </div>

          {canManage ? (
            <div className="flex items-center gap-2">
              {dirty ? (
                <Btn small onClick={onDiscard} disabled={saving}>
                  <RotateCcw className="size-4" /> {t("marketing.discard")}
                </Btn>
              ) : null}
              <Btn
                small
                onClick={onToggleStatus}
                disabled={toggleDisabled}
                title={toggleDisabled ? t("marketing.ready.blockedHint") : undefined}
              >
                {running ? <Pause className="size-4" /> : <Play className="size-4" />}
                {t(running ? "marketing.pause" : "marketing.activate")}
              </Btn>
              <Btn small tone={dirty ? "brand" : "plain"} onClick={onSave} disabled={!dirty || saving}>
                {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                {t(dirty ? "marketing.save" : "marketing.saved")}
              </Btn>
            </div>
          ) : null}
        </div>
      </div>

      {/* A quiet strip rather than a toast: unsaved work is a state, not an
          event, and it has to stay visible for as long as it is true. */}
      {dirty ? (
        <p
          className="mt-2.5 flex items-center gap-2 text-[11.5px] font-[550]"
          style={{ color: "color-mix(in oklab, var(--color-warning) 52%, var(--color-foreground))" }}
        >
          <span className="size-1.5 rounded-full" style={{ background: "var(--color-warning)" }} />
          {t("marketing.unsaved")}
        </p>
      ) : null}
    </header>
  );
}
